import React, { useEffect, useState } from "react";
import SEO from "@/components/SEO";
import PageHeader from "@/components/PageHeader";
import PhoneInput from "@/components/PhoneInput";
import { api, formatAxiosError } from "@/lib/api";
import { RECAPTCHA_SITE_KEY, loadRecaptchaScript, getRecaptchaToken } from "@/lib/recaptcha";
import { RecaptchaDisclosure } from "@/pages/Demo";
import { toast } from "sonner";
import { Mail, Phone, MapPin, User, AtSign, MessageSquare, Type } from "lucide-react";
import { useT } from "@/hooks/useT";

export default function Kontakt() {
  const t = useT();
  useEffect(() => { loadRecaptchaScript(); }, []);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [country, setCountry] = useState(null); // { name, iso, dial }
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const captcha_token = await getRecaptchaToken("contact_submit");
      if (RECAPTCHA_SITE_KEY && !captcha_token) {
        toast.error(t("Verifikimi i sigurisë dështoi. Ju lutem provoni sërish.", "Security check failed. Please try again."));
        setLoading(false);
        return;
      }
      const dial = country?.dial ? `+${country.dial} ` : "";
      const payload = {
        name: `${form.firstName} ${form.lastName}`.trim(),
        email: form.email,
        phone: form.phone ? `${dial}${form.phone}`.trim() : "",
        subject: form.subject,
        message: form.message,
        captcha_token,
      };
      await api.post("/contact-requests", payload);
      // GA4 conversion event
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "contact_submit", {
          event_category: "engagement",
          event_label: form.subject || "contact_form",
        });
      }
      setSent(true);
      toast.success(t("Mesazhi u dërgua. Do t'ju kontaktojmë së shpejti.", "Message sent. We'll get back to you shortly."));
      setForm({ firstName: "", lastName: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatAxiosError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title={t("Kontakt — Akomoda", "Contact — Akomoda")}
        description={t(
          "Na shkruani për pyetje, çmime ose mbështetje. Ekipi i Akomoda ju përgjigjet brenda 24 orëve.",
          "Send us a message about pricing, questions or support. The Akomoda team replies within 24 hours."
        )}
      />
      <PageHeader
        eyebrow={t("Kontakt", "Contact")}
        title={t("Na shkruani", "Get in touch")}
        description={t(
          "Pyetje për sistemin, për çmimet, ose dëshironi një demo? Ekipi ynë ju përgjigjet brenda 24 orëve gjatë ditëve të punës.",
          "Questions about the system, pricing, or want a demo? Our team replies within 24 business hours."
        )}
        breadcrumb={[{ label: t("Kontakt", "Contact") }]}
      />

      <section className="bg-surface-1">
        <div className="container-akm grid gap-10 py-14 md:grid-cols-12 md:py-20">
          <div className="md:col-span-5">
            <h2 className="h2-akm">{t("Si mund t'ju ndihmojmë?", "How can we help?")}</h2>
            <p className="lead mt-3">
              {t(
                "Plotësoni formën ose na shkruani direkt me email. Përgjigjemi brenda 24 orëve gjatë ditëve të punës.",
                "Fill in the form or email us directly. We respond within 24 business hours."
              )}
            </p>
            <ul className="mt-6 space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <span className="grid h-9 w-9 place-items-center rounded-md border border-line bg-bg text-[#FF7A00]">
                  <Mail size={16} />
                </span>
                <div>
                  <div className="font-semibold text-ink">Email</div>
                  <a href="mailto:info@akomoda.co" className="link-akm">info@akomoda.co</a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="grid h-9 w-9 place-items-center rounded-md border border-line bg-bg text-[#FF7A00]">
                  <Phone size={16} />
                </span>
                <div>
                  <div className="font-semibold text-ink">{t("Nr. Cel", "Phone No.")}</div>
                  <span className="text-slate2">{t("E hënë – e premte, 09:00–18:00", "Monday – Friday, 09:00–18:00")}</span>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="grid h-9 w-9 place-items-center rounded-md border border-line bg-bg text-[#FF7A00]">
                  <MapPin size={16} />
                </span>
                <div>
                  <div className="font-semibold text-ink">{t("Ku ndodhemi", "Where we are")}</div>
                  <span className="text-slate2">{t("Tiranë, Shqipëri", "Tirana, Albania")}</span>
                </div>
              </li>
            </ul>
          </div>

          <form onSubmit={submit} className="md:col-span-7" data-testid="contact-form">
            {sent && (
              <div className="mb-4 rounded-md border border-[#DCFCE7] bg-[#F0FDF4] p-3 text-sm text-[#166534]" data-testid="contact-success">
                {t("Faleminderit! Mesazhi shkoi. Do t'ju kthehemi së shpejti.", "Thanks! Your message is on its way. We'll be in touch shortly.")}
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <IconField
                icon={User}
                placeholder={t("Emri *", "First name *")}
                value={form.firstName}
                onChange={set("firstName")}
                required
                name="firstName"
                autoComplete="given-name"
                testid="contact-firstname"
              />
              <IconField
                icon={User}
                placeholder={t("Mbiemri *", "Last name *")}
                value={form.lastName}
                onChange={set("lastName")}
                required
                name="lastName"
                autoComplete="family-name"
                testid="contact-lastname"
              />
              <IconField
                icon={AtSign}
                placeholder="Email *"
                type="email"
                value={form.email}
                onChange={set("email")}
                required
                name="email"
                autoComplete="email"
                testid="contact-email"
              />
              <PhoneInput
                value={form.phone}
                onChange={set("phone")}
                country={country}
                onCountryChange={setCountry}
                placeholder={t("Nr. Cel", "Phone No.")}
                testid="contact-phone"
              />
              <div className="sm:col-span-2">
                <IconField
                  icon={Type}
                  placeholder={t("Subjekti", "Subject")}
                  value={form.subject}
                  onChange={set("subject")}
                  name="subject"
                  testid="contact-subject"
                />
              </div>
            </div>

            <div className="relative mt-3">
              <MessageSquare size={16} className="pointer-events-none absolute left-3 top-3 text-slate3" />
              <textarea
                required
                value={form.message}
                onChange={set("message")}
                rows={5}
                placeholder={t("Mesazhi juaj *", "Your message *")}
                className="w-full rounded-md border border-line bg-white py-3 pl-9 pr-3 text-sm text-ink outline-none placeholder:text-slate3/70 focus:border-[#FF7A00] focus:ring-2 focus:ring-[#FF7A00]/20"
                data-testid="contact-message"
              />
            </div>

            <RecaptchaDisclosure t={t} />

            <button type="submit" disabled={loading} className="btn-primary mt-5 disabled:opacity-60" data-testid="contact-submit">
              {loading ? t("Duke dërguar…", "Sending…") : t("Dërgo mesazhin", "Send message")}
            </button>
          </form>
        </div>
      </section>
    </>
  );
}

function IconField({ icon: Icon, testid, ...rest }) {
  return (
    <div className="relative">
      <Icon size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate3" />
      <input
        {...rest}
        className="w-full rounded-md border border-line bg-white py-2.5 pl-9 pr-3 text-sm text-ink outline-none placeholder:text-slate3/70 focus:border-[#FF7A00] focus:ring-2 focus:ring-[#FF7A00]/20"
        data-testid={testid}
      />
    </div>
  );
}
